import { Box, Button, Flex, Loading, Text, createColumnHelper } from 'ui'
import { useMemo } from 'react'
import { usePulseContext } from '../../context/PulseProvider'
import { useFillPayNoticeByLocation } from 'queries/pulse/useFillPayNoticeByLocation'
import { FillPayNoticeByLocation as FillPayNoticeByLocationType } from 'api/pulse/fillPayNoticeByLocation'
import { formatCurrency, formatNumber, formatPercentage } from 'lib/string'
import { InfiniteTable } from '../InfiniteTable'
import { useFillPayNoticeByLocationDownloadCsv } from 'queries/pulse/useFillPayNoticeByLocationDownloadCsv'

const columnHelper = createColumnHelper<FillPayNoticeByLocationType[0]>()

const columns = [
  columnHelper.accessor('company_name', {
    header: 'Location Name',
  }),
  columnHelper.accessor('workers_needed_final', {
    header: '# Requests',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatNumber(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('late_notice_shifts', {
    header: '% Late Notice Shifts',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatPercentage(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('avg_pay_short_notice', {
    header: 'Avg Pay Short Notice',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatCurrency(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('avg_pay_long_notice', {
    header: 'Avg Pay Long Notice',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatCurrency(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('change_in_pay_long_vs_short_notice', {
    header: '% Change in Pay Long vs Short Notice',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatPercentage(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('fill_rate_short_notice', {
    header: '% Fill Rate Short Notice',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatPercentage(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('fill_rate_long_notice', {
    header: '% Fill Rate Long Notice',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatPercentage(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('change_in_fill_long_vs_short_notice', {
    header: '% Change in Fill Long vs Short Notice',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatPercentage(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
]

export function FillPayNoticeByLocation() {
  const {
    state: { filters },
  } = usePulseContext()

  const filtersWithPagination = useMemo(() => {
    return {
      ...filters,
      page: 0,
      pageSize: 50,
    }
  }, [filters])

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useFillPayNoticeByLocation(filtersWithPagination)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    useFillPayNoticeByLocationDownloadCsv(filters)

  const flatData = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? []
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Pay and Fill Performance vs Notice by Location
          </Text>
          <Text fontSize={2} color="lightText">
            Inter-location evaluation with respect to notice trends, pay rates
            and subsequent fill impact.
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <InfiniteTable
          data={flatData}
          columns={columns}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          height="400px"
        />
      )}
    </Box>
  )
}
